/* ==========================================================================
   NKB SOLUTIONS — PREMIUM BLOG ENGINE
   premium-blog.js
   Vanilla JS only. No dependencies. Runs once per article page.
   ========================================================================== */

(function () {
  "use strict";

  document.addEventListener("DOMContentLoaded", function () {
    var root = document.querySelector(".pb-scope");
    if (!root) return;

    initReadingProgress(root);
    initContentUpgrades(root);
    initHeadingAnchors(root);
    initScrollReveal(root);
    initStatCounters(root);
    initTimelineReveal(root);
    initFAQ(root);
    initShare(root);
    initNewsletter(root);
  });

  /* ------------------------------------------------------------------
     Reading progress bar
     ------------------------------------------------------------------ */
  function initReadingProgress(root) {
    var bar = root.querySelector("[data-pb-progress-bar]");
    var article = root.querySelector("[data-pb-article-body]");
    if (!bar || !article) return;

    function update() {
      var rect = article.getBoundingClientRect();
      var articleTop = rect.top + window.scrollY;
      var articleHeight = article.offsetHeight;
      var viewportHeight = window.innerHeight;
      var scrolled = window.scrollY - articleTop + viewportHeight * 0.5;
      var total = articleHeight;
      var percent = Math.min(100, Math.max(0, (scrolled / total) * 100));
      bar.style.width = percent + "%";
    }

    window.addEventListener("scroll", throttle(update, 50), { passive: true });
    window.addEventListener("resize", throttle(update, 200));
    update();
  }

  /* ------------------------------------------------------------------
     Auto-upgrade raw author-entered HTML:
     - tables -> responsive wrapper + data-label per cell
     - images -> lazy loading
     ------------------------------------------------------------------ */
  function initContentUpgrades(root) {
    var article = root.querySelector("[data-pb-article-body]");
    if (!article) return;

    // Tables
    var tables = article.querySelectorAll("table");
    tables.forEach(function (table) {
      if (table.closest(".pb-table-wrap")) return;

      var headers = [];
      var headRow = table.querySelector("thead tr");
      if (headRow) {
        headers = Array.prototype.map.call(headRow.children, function (th) {
          return th.textContent.trim();
        });
      }

      var bodyRows = table.querySelectorAll("tbody tr");
      bodyRows.forEach(function (row) {
        Array.prototype.forEach.call(row.children, function (cell, i) {
          if (headers[i]) cell.setAttribute("data-label", headers[i]);
        });
      });

      var wrap = document.createElement("div");
      wrap.className = "pb-table-wrap";
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });

    // Images
    var images = article.querySelectorAll("img:not([loading])");
    images.forEach(function (img) {
      img.setAttribute("loading", "lazy");
      img.setAttribute("decoding", "async");
    });
  }

  /* ------------------------------------------------------------------
     Assign stable anchor IDs to H2/H3s so headings stay deep-linkable
     (e.g. from search results or external links) even without a visible
     table of contents.
     ------------------------------------------------------------------ */
  function initHeadingAnchors(root) {
    var article = root.querySelector("[data-pb-article-body]");
    if (!article) return;

    var headings = article.querySelectorAll("h2, h3");
    var usedIds = {};

    headings.forEach(function (heading) {
      if (heading.id) return;
      var base = slugify(heading.textContent) || "section";
      var id = base;
      var n = 1;
      while (usedIds[id]) {
        id = base + "-" + n;
        n++;
      }
      usedIds[id] = true;
      heading.id = id;
    });
  }

  /* ------------------------------------------------------------------
     Generic scroll-reveal (fade-in / slide-up)
     ------------------------------------------------------------------ */
  function initScrollReveal(root) {
    var items = root.querySelectorAll(".pb-reveal, .pb-callout, .pb-product-card, .pb-author-box");
    if (!("IntersectionObserver" in window) || !items.length) {
      items.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    items.forEach(function (el) {
      el.classList.add("pb-reveal");
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------
     Animated statistic counters
     ------------------------------------------------------------------ */
  function initStatCounters(root) {
    var cards = root.querySelectorAll("[data-pb-stat-value]");
    if (!cards.length) return;

    var animated = new WeakSet();

    function animateCard(card) {
      if (animated.has(card)) return;
      animated.add(card);
      card.closest(".pb-stat-card").classList.add("is-visible");

      var raw = card.getAttribute("data-pb-stat-value");
      var match = raw.match(/^([^\d]*)([\d.]+)(.*)$/);
      if (!match) {
        card.textContent = raw;
        return;
      }
      var prefix = match[1];
      var target = parseFloat(match[2]);
      var suffix = match[3];
      var isDecimal = match[2].indexOf(".") !== -1;
      var duration = 1200;
      var start = null;

      function step(ts) {
        if (!start) start = ts;
        var progress = Math.min(1, (ts - start) / duration);
        var eased = 1 - Math.pow(1 - progress, 3);
        var value = target * eased;
        card.textContent = prefix + (isDecimal ? value.toFixed(1) : Math.round(value)) + suffix;
        if (progress < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    if (!("IntersectionObserver" in window)) {
      cards.forEach(animateCard);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animateCard(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    cards.forEach(function (card) { observer.observe(card); });
  }

  /* ------------------------------------------------------------------
     Timeline reveal
     ------------------------------------------------------------------ */
  function initTimelineReveal(root) {
    var steps = root.querySelectorAll(".pb-timeline__step");
    if (!steps.length) return;

    if (!("IntersectionObserver" in window)) {
      steps.forEach(function (s) { s.classList.add("is-visible"); });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry, i) {
          if (entry.isIntersecting) {
            setTimeout(function () {
              entry.target.classList.add("is-visible");
            }, i * 120);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    steps.forEach(function (step) { observer.observe(step); });
  }

  /* ------------------------------------------------------------------
     FAQ accordion — accessible, keyboard operable
     ------------------------------------------------------------------ */
  function initFAQ(root) {
    var items = root.querySelectorAll(".pb-faq-item");
    items.forEach(function (item) {
      var button = item.querySelector(".pb-faq-item__question");
      var answer = item.querySelector(".pb-faq-item__answer");
      if (!button || !answer) return;

      button.addEventListener("click", function () {
        var isOpen = item.classList.contains("is-open");

        if (isOpen) {
          answer.style.maxHeight = null;
          item.classList.remove("is-open");
          button.setAttribute("aria-expanded", "false");
        } else {
          item.classList.add("is-open");
          button.setAttribute("aria-expanded", "true");
          answer.style.maxHeight = answer.scrollHeight + "px";
        }
      });
    });

    window.addEventListener("resize", throttle(function () {
      root.querySelectorAll(".pb-faq-item.is-open .pb-faq-item__answer").forEach(function (answer) {
        answer.style.maxHeight = answer.scrollHeight + "px";
      });
    }, 200));
  }

  /* ------------------------------------------------------------------
     Share buttons
     ------------------------------------------------------------------ */
  function initShare(root) {
    var shareBlock = root.querySelector("[data-pb-share]");
    if (!shareBlock) return;

    var pageUrl = window.location.href;
    var pageTitle = document.title;

    var buttons = shareBlock.querySelectorAll("[data-pb-share-action]");
    buttons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var action = btn.getAttribute("data-pb-share-action");

        if (action === "copy") {
          navigator.clipboard.writeText(pageUrl).then(function () {
            btn.setAttribute("data-copied", "true");
            var label = btn.getAttribute("aria-label");
            btn.setAttribute("aria-label", "Link copied");
            setTimeout(function () {
              btn.removeAttribute("data-copied");
              btn.setAttribute("aria-label", label);
            }, 2000);
          });
          return;
        }

        var shareUrls = {
          facebook: "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(pageUrl),
          x: "https://twitter.com/intent/tweet?url=" + encodeURIComponent(pageUrl) + "&text=" + encodeURIComponent(pageTitle),
          linkedin: "https://www.linkedin.com/sharing/share-offsite/?url=" + encodeURIComponent(pageUrl),
          whatsapp: "https://api.whatsapp.com/send?text=" + encodeURIComponent(pageTitle + " " + pageUrl)
        };

        if (shareUrls[action]) {
          window.open(shareUrls[action], "_blank", "noopener,noreferrer,width=600,height=600");
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Newsletter form (Shopify customer/contact form compatible)
     ------------------------------------------------------------------ */
  function initNewsletter(root) {
    var forms = root.querySelectorAll("[data-pb-newsletter-form]");
    forms.forEach(function (form) {
      form.addEventListener("submit", function () {
        var wrapper = form.closest(".pb-newsletter");
        if (wrapper) {
          setTimeout(function () {
            wrapper.classList.add("is-submitted");
          }, 50);
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------ */
  function throttle(fn, wait) {
    var last = 0;
    var timeout = null;
    return function () {
      var now = Date.now();
      var args = arguments;
      var context = this;
      if (now - last >= wait) {
        last = now;
        fn.apply(context, args);
      } else {
        clearTimeout(timeout);
        timeout = setTimeout(function () {
          last = Date.now();
          fn.apply(context, args);
        }, wait - (now - last));
      }
    };
  }

  function slugify(text) {
    return text
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  }
})();
